// Configs — per-namespace editor for app.platform_config.
//
// Each namespace stores an unstructured JSON blob server-side. Operators
// edit it through one of three UIs:
//   - SmsSettingsEditor / EmailSettingsEditor — hand-built rich editors
//     with test-send buttons (kept as-is for the specialised flows)
//   - SchemaForm — schema-driven typed form for the 15 other namespaces;
//     descriptors live in apps/backoffice/src/lib/configs/
//   - ConfigEditor JSON-textarea fallback — only triggers when no schema
//     is registered for the namespace
//
// Unknown keys (set via the legacy JSON editor before the schema landed)
// are preserved on save: the form value is shallow-merged INTO the
// original blob, never replacing it.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader, ToolBtn } from '@drivecars/admin-ui';
import { SchemaForm, type FieldErrors, type SchemaValues } from '@/components/SchemaForm';
import { CurrencyRatesPanel } from '@/components/CurrencyRatesPanel';
import { PairRateMatrixPanel } from '@/components/PairRateMatrixPanel';
import { getSchema, listSchemas } from '@/lib/configs';
import {
  downloadConfigsCsv,
  fetchAdminConfig,
  fetchAdminConfigs,
  fetchEmailTransportConfig,
  saveAdminConfig,
  testSendEmail,
  testSendSms,
  testSendWhatsApp,
  type WhatsAppTestVars,
} from '@/lib/admin-api';

// Source of truth for namespace labels: the registered schemas. Anything
// outside the registry (special editors, future legacy blobs) gets a
// hand-coded entry here so the sidebar still has a friendly name.
const NS_LABEL_OVERRIDES: Record<string, string> = {
  // SmsSettingsEditor + EmailSettingsEditor are custom — schema labels
  // would be ignored for these in practice, but kept here for parity.
  'sms-settings': 'SMS',
  'email-settings': 'Email',
};

function nsLabel(ns: string): string {
  if (NS_LABEL_OVERRIDES[ns]) return NS_LABEL_OVERRIDES[ns]!;
  return getSchema(ns)?.label ?? ns;
}

// Static catalog of every namespace the UI knows about — schemas + the
// custom-editor namespaces. Used to render a sidebar entry even when the
// server hasn't seen a save for that namespace yet.
const NS_CATALOG = (() => {
  const set = new Set(listSchemas().map((s) => s.namespace));
  set.add('sms-settings');
  set.add('email-settings');
  return Array.from(set);
})();

function formatRelTime(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function Configs() {
  const list = useQuery({
    queryKey: ['admin', 'configs', 'list'],
    queryFn: ({ signal }) => fetchAdminConfigs(signal),
  });

  // Merge server rows with the static catalog of known namespaces so that
  // a never-saved namespace (no DB row yet) is still visible + selectable.
  // The first save UPSERTs the row server-side, after which it'll show up
  // in the server list with a real updatedAt.
  const namespaces = useMemo(() => {
    const serverRows = list.data?.rows ?? [];
    const seen = new Set(serverRows.map((r) => r.namespace));
    const stubs = NS_CATALOG
      .filter((ns) => !seen.has(ns))
      .map((ns) => ({ namespace: ns, value: null, updatedAt: null, updatedBy: null }));
    return [...serverRows, ...stubs].sort((a, b) =>
      nsLabel(a.namespace).localeCompare(nsLabel(b.namespace)),
    );
  }, [list.data]);

  const [selected, setSelected] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Default-select the first namespace once the list loads.
  useEffect(() => {
    if (!selected && namespaces.length > 0) {
      setSelected(namespaces[0]!.namespace);
    }
  }, [selected, namespaces]);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="System"
        title="Configs"
        sub={`${namespaces.length} namespaces. Edits propagate to apps/web within ~60 seconds.`}
        actions={
          <ToolBtn
            icon="download"
            onClick={() => {
              setExporting(true);
              void downloadConfigsCsv().finally(() => setExporting(false));
            }}
          >
            {exporting ? 'Exporting…' : 'Export all'}
          </ToolBtn>
        }
      />
      {list.error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load configs: {(list.error as Error).message}
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white lg:col-span-4">
          <ul className="divide-y divide-[color:var(--bo-line)]">
            {list.isLoading ? (
              <li className="px-4 py-3 text-[12px] text-[color:var(--bo-mute)]">
                Loading namespaces…
              </li>
            ) : null}
            {namespaces.map((row) => {
              const active = row.namespace === selected;
              return (
                <li key={row.namespace}>
                  <button
                    type="button"
                    onClick={() => setSelected(row.namespace)}
                    className={`flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition ${
                      active
                        ? 'bg-[color:var(--bo-bg)]'
                        : 'hover:bg-[color:var(--bo-bg)]'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-semibold text-[color:var(--bo-ink)]">
                        {nsLabel(row.namespace)}
                      </div>
                      <div className="truncate font-mono text-[10.5px] text-[color:var(--bo-mute)]">
                        {row.namespace}
                      </div>
                    </div>
                    <span className="shrink-0 text-[10.5px] tabular-nums text-[color:var(--bo-mute)]">
                      {formatRelTime(row.updatedAt)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="lg:col-span-8">
          {selected ? (
            selected === 'sms-settings' ? (
              <SmsSettingsEditor />
            ) : selected === 'email-settings' ? (
              <EmailSettingsEditor />
            ) : (
              <ConfigEditor namespace={selected} />
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface EditorProps {
  namespace: string;
}

function ConfigEditor({ namespace }: EditorProps) {
  const schema = getSchema(namespace);
  return schema ? (
    <SchemaConfigEditor namespace={namespace} />
  ) : (
    <JsonConfigEditor namespace={namespace} />
  );
}

// Schema-driven editor — used for any namespace registered in
// lib/configs/. Renders typed inputs via SchemaForm; saves the merged
// blob (form values shallow-overlay the original) so unknown keys live on.
function SchemaConfigEditor({ namespace }: EditorProps) {
  const schema = getSchema(namespace)!;
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['admin', 'configs', namespace],
    queryFn: ({ signal }) => fetchAdminConfig(namespace, signal),
  });

  const initial: SchemaValues = useMemo(() => {
    const v = detail.data?.value;
    return v && typeof v === 'object' && !Array.isArray(v)
      ? (v as SchemaValues)
      : {};
  }, [detail.data]);

  const [values, setValues] = useState<SchemaValues>(initial);

  useEffect(() => {
    setValues(initial);
  }, [initial]);

  const saveMut = useMutation({
    mutationFn: ({ value }: { value: SchemaValues }) =>
      saveAdminConfig(namespace, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'configs', namespace] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'configs', 'list'] });
    },
  });

  // Stable string-comparison for dirty state. JSON.stringify is fine for
  // the small object sizes here (a few KB at most).
  const dirty = JSON.stringify(values) !== JSON.stringify(initial);

  // Shallow merge into the original blob — preserves keys the schema
  // doesn't know about (legacy raw-JSON edits, future fields).
  const handleSave = () => {
    const merged = { ...initial, ...values };
    saveMut.mutate({ value: merged });
  };

  // Pull field-level errors from a 422 response. The API returns
  // `{ error, namespace, issues: { fieldErrors: { key: string[] } } }`.
  const fieldErrors: FieldErrors = useMemo(() => {
    const err = saveMut.error as { status?: number; body?: unknown } | null;
    if (!err || err.status !== 422 || !err.body || typeof err.body !== 'object') return {};
    const issues = (err.body as { issues?: { fieldErrors?: Record<string, string[]> } }).issues;
    return issues?.fieldErrors ?? {};
  }, [saveMut.error]);

  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--bo-line)] px-5 py-3">
        <div>
          <div className="text-[14px] font-semibold">{schema.label}</div>
          <div className="font-mono text-[11px] text-[color:var(--bo-mute)]">
            {namespace}
          </div>
          {schema.description ? (
            <p className="mt-1 max-w-2xl text-[12px] text-[color:var(--bo-mute)]">
              {schema.description}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setValues(initial)}
            disabled={!dirty}
            className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saveMut.isPending}
            className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {schema.unconsumed ? (
        <div className="border-b border-[color:var(--bo-line)] bg-[#fefce8] px-5 py-2 text-[12px] text-[#854d0e]">
          Heads up — this namespace isn&apos;t read by any app yet. Edits save fine, but
          nothing in apps/web|cms|api consumes the values. Wire consumption first, then
          drop <code className="mx-1 font-mono text-[11px]">unconsumed: true</code> from
          the schema file.
        </div>
      ) : null}
      {detail.error ? (
        <div className="px-5 py-3 text-[12.5px] text-[#9f1239]">
          Couldn&apos;t load namespace: {(detail.error as Error).message}
        </div>
      ) : null}
      {saveMut.error ? (
        <div className="border-b border-[color:var(--bo-line)] bg-[#fff1f2] px-5 py-2 text-[12.5px] text-[#9f1239]">
          {(saveMut.error as { status?: number }).status === 422
            ? 'Some fields are invalid — see the highlighted entries below.'
            : `Save failed: ${(saveMut.error as Error).message}`}
        </div>
      ) : null}
      {saveMut.isSuccess && !dirty ? (
        <div className="border-b border-[color:var(--bo-line)] bg-[#dcfce7] px-5 py-2 text-[12.5px] text-[#166534]">
          Saved.
        </div>
      ) : null}
      <div className="px-5 py-5">
        <SchemaForm
          schema={schema}
          values={values}
          onChange={setValues}
          disabled={detail.isLoading || saveMut.isPending}
          errors={fieldErrors}
        />
        {namespace === 'currency-settings' ? <CurrencyRatesPanel /> : null}
        {namespace === 'currency-settings' ? <PairRateMatrixPanel /> : null}
      </div>
    </div>
  );
}

// Raw-JSON fallback — kept for any future namespace not yet covered by a
// schema descriptor. Operators see a "no schema" banner so they know it
// hasn't been polished yet.
function JsonConfigEditor({ namespace }: EditorProps) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['admin', 'configs', namespace],
    queryFn: ({ signal }) => fetchAdminConfig(namespace, signal),
  });

  const initialJson = useMemo(() => {
    if (!detail.data) return '';
    return JSON.stringify(detail.data.value ?? {}, null, 2);
  }, [detail.data]);

  const [draft, setDraft] = useState<string>('');
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(initialJson);
    setParseError(null);
  }, [initialJson]);

  const saveMut = useMutation({
    mutationFn: ({ value }: { value: unknown }) => saveAdminConfig(namespace, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'configs', namespace] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'configs', 'list'] });
    },
  });

  const dirty = draft !== initialJson;
  const handleSave = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch (err) {
      setParseError((err as Error).message);
      return;
    }
    setParseError(null);
    saveMut.mutate({ value: parsed });
  };

  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--bo-line)] px-5 py-3">
        <div>
          <div className="text-[14px] font-semibold">{nsLabel(namespace)}</div>
          <div className="font-mono text-[11px] text-[color:var(--bo-mute)]">{namespace}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setDraft(initialJson);
              setParseError(null);
            }}
            disabled={!dirty}
            className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saveMut.isPending}
            className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      <div className="border-b border-[color:var(--bo-line)] bg-[#fefce8] px-5 py-2 text-[12px] text-[#854d0e]">
        No typed schema for this namespace yet — raw JSON only. Add one in
        <code className="mx-1 font-mono text-[11px]">apps/backoffice/src/lib/configs/</code>.
      </div>
      {detail.error ? (
        <div className="px-5 py-3 text-[12.5px] text-[#9f1239]">
          Couldn&apos;t load namespace: {(detail.error as Error).message}
        </div>
      ) : null}
      {parseError ? (
        <div className="border-b border-[color:var(--bo-line)] bg-[#fff1f2] px-5 py-2 text-[12.5px] text-[#9f1239]">
          Invalid JSON: {parseError}
        </div>
      ) : null}
      {saveMut.error ? (
        <div className="border-b border-[color:var(--bo-line)] bg-[#fff1f2] px-5 py-2 text-[12.5px] text-[#9f1239]">
          Save failed: {(saveMut.error as Error).message}
        </div>
      ) : null}
      {saveMut.isSuccess && !dirty ? (
        <div className="border-b border-[color:var(--bo-line)] bg-[#dcfce7] px-5 py-2 text-[12.5px] text-[#166534]">
          Saved.
        </div>
      ) : null}
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        spellCheck={false}
        className="h-[600px] w-full resize-none border-0 bg-white px-5 py-4 font-mono text-[12.5px] leading-relaxed outline-none"
      />
    </div>
  );
}

// ─── SMS settings — structured form ─────────────────────────────────

interface SmsSettingsValue {
  enabled?: boolean;
  adminPhone?: string;
  adminCountry?: string;
  driver?: string;
  sendTo?: string;
  twilio?: Record<string, unknown>;
  eventCreateBooking?: SmsAudienceToggleSet;
  eventUpdateBooking?: SmsAudienceToggleSet;
}

interface SmsAudienceToggleSet {
  admin?: { enabled?: boolean };
  vendor?: { enabled?: boolean };
  customer?: { enabled?: boolean };
}

const COUNTRY_OPTIONS: Array<{ code: string; label: string }> = [
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'MA', label: 'Morocco' },
  { code: 'ES', label: 'Spain' },
  { code: 'US', label: 'United States' },
];

function SmsSettingsEditor() {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['admin', 'configs', 'sms-settings'],
    queryFn: ({ signal }) => fetchAdminConfig('sms-settings', signal),
  });

  const initial = useMemo<SmsSettingsValue>(
    () => (detail.data?.value as SmsSettingsValue | null) ?? {},
    [detail.data],
  );
  const [draft, setDraft] = useState<SmsSettingsValue>(initial);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const saveMut = useMutation({
    mutationFn: ({ value }: { value: SmsSettingsValue }) =>
      saveAdminConfig('sms-settings', value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'configs', 'sms-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'configs', 'list'] });
    },
  });

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);

  const setField = <K extends keyof SmsSettingsValue>(key: K, value: SmsSettingsValue[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };
  const setEventToggle = (
    event: 'eventCreateBooking' | 'eventUpdateBooking',
    audience: 'admin' | 'vendor' | 'customer',
    enabled: boolean,
  ) => {
    setDraft((d) => ({
      ...d,
      [event]: {
        ...(d[event] ?? {}),
        [audience]: { enabled },
      },
    }));
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--bo-line)] px-5 py-3">
          <div>
            <div className="text-[14px] font-semibold">SMS</div>
            <div className="font-mono text-[11px] text-[color:var(--bo-mute)]">sms-settings</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDraft(initial)}
              disabled={!dirty}
              className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => saveMut.mutate({ value: draft })}
              disabled={!dirty || saveMut.isPending}
              className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        {saveMut.error ? (
          <div className="border-b border-[color:var(--bo-line)] bg-[#fff1f2] px-5 py-2 text-[12.5px] text-[#9f1239]">
            Save failed: {(saveMut.error as Error).message}
          </div>
        ) : null}
        {saveMut.isSuccess && !dirty ? (
          <div className="border-b border-[color:var(--bo-line)] bg-[#dcfce7] px-5 py-2 text-[12.5px] text-[#166534]">
            Saved.
          </div>
        ) : null}

        <div className="space-y-6 px-5 py-5">
          <Section title="Status" desc="Master switch — when off, no SMS is sent regardless of toggles.">
            <Checkbox
              label="SMS enabled"
              checked={Boolean(draft.enabled)}
              onChange={(v) => setField('enabled', v)}
            />
          </Section>

          <Section
            title="Administrator"
            desc="Phone the platform admin receives operational SMS at. E.164: +<country><number>, e.g. +12019480710."
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField
                label="Admin phone"
                placeholder="+12019480710"
                value={draft.adminPhone ?? ''}
                onChange={(v) => setField('adminPhone', v)}
                hint="Must be E.164 (plus sign, country code, number)."
              />
              <SelectField
                label="Admin country"
                value={draft.adminCountry ?? ''}
                onChange={(v) => setField('adminCountry', v)}
                options={[{ code: '', label: '— Select —' }, ...COUNTRY_OPTIONS]}
              />
            </div>
          </Section>

          <Section
            title="Create booking"
            desc="Audiences who receive an SMS when a draft booking transitions to confirmed."
          >
            <AudienceToggles
              value={draft.eventCreateBooking}
              onChange={(audience, enabled) =>
                setEventToggle('eventCreateBooking', audience, enabled)
              }
            />
          </Section>

          <Section
            title="Update booking"
            desc="Audiences who receive an SMS on cancellation / status change."
          >
            <AudienceToggles
              value={draft.eventUpdateBooking}
              onChange={(audience, enabled) =>
                setEventToggle('eventUpdateBooking', audience, enabled)
              }
            />
          </Section>

          <Section
            title="Driver"
            desc="Twilio is the only supported provider today. Credentials live in the apps/api .env (TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_FROM_NUMBER) — not editable here for security."
          >
            <div className="rounded-md border border-dashed border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-4 py-3 text-[12.5px] text-[color:var(--bo-mute)]">
              Driver: <span className="font-mono text-[color:var(--bo-ink)]">twilio</span>
            </div>
          </Section>
        </div>
      </div>

      <SmsTestPanel adminPhone={draft.adminPhone} />
      <WhatsAppTestPanel adminPhone={draft.adminPhone} />
    </div>
  );
}

function Section({
  title,
  desc,
  children,
}: {
  title: string;
  desc?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[13px] font-semibold text-[color:var(--bo-ink)]">{title}</div>
      {desc ? (
        <div className="mt-0.5 text-[11.5px] text-[color:var(--bo-mute)]">{desc}</div>
      ) : null}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  type?: 'text' | 'tel';
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] focus:border-[color:var(--bo-mute)] focus:outline-none"
      />
      {hint ? <span className="text-[11px] text-[color:var(--bo-mute)]">{hint}</span> : null}
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ code: string; label: string }>;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] focus:border-[color:var(--bo-mute)] focus:outline-none"
      >
        {options.map((opt) => (
          <option key={opt.code} value={opt.code}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-[13px]">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-[color:var(--bo-line)]"
      />
      <span className="text-[color:var(--bo-ink)]">{label}</span>
    </label>
  );
}

function AudienceToggles({
  value,
  onChange,
}: {
  value?: SmsAudienceToggleSet;
  onChange: (audience: 'admin' | 'vendor' | 'customer', enabled: boolean) => void;
}) {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2">
      <Checkbox
        label="Administrator"
        checked={Boolean(value?.admin?.enabled)}
        onChange={(v) => onChange('admin', v)}
      />
      <Checkbox
        label="Vendor"
        checked={Boolean(value?.vendor?.enabled)}
        onChange={(v) => onChange('vendor', v)}
      />
      <Checkbox
        label="Customer"
        checked={Boolean(value?.customer?.enabled)}
        onChange={(v) => onChange('customer', v)}
      />
    </div>
  );
}

// Friendly message for the SMS test, mapping the API's reason/status to plain
// English instead of a raw "Failed: 503".
function smsErrorMessage(err: unknown): string {
  const e = err as { status?: number; body?: { error?: string } };
  const reason = e?.body?.error;
  if (e?.status === 503 || reason === 'not_configured') {
    return 'SMS is not configured on this environment (Twilio credentials missing).';
  }
  if (reason === 'invalid_to') return 'Invalid phone number — use E.164 format, e.g. +12019480710.';
  if (reason === 'twilio_error') return 'Twilio rejected the message — check the number and Twilio account.';
  if (reason) return `Failed: ${reason}`;
  return `Failed: ${(err as Error).message}`;
}

function whatsAppErrorMessage(err: unknown): string {
  const e = err as { status?: number; body?: { error?: string } };
  const reason = e?.body?.error;
  if (e?.status === 503 || reason === 'not_configured') {
    return 'WhatsApp is not configured (WHATSAPP_BID_TEMPLATE_SID or Twilio credentials missing).';
  }
  if (reason === 'invalid_to') return 'Invalid phone number — use E.164 format, e.g. +12019480710.';
  if (reason === 'twilio_error') return 'Twilio rejected the message — check the number, sender, and that the template is approved.';
  if (reason) return `Failed: ${reason}`;
  return `Failed: ${(err as Error).message}`;
}

function SmsTestPanel({ adminPhone }: { adminPhone?: string }) {
  const [to, setTo] = useState(adminPhone ?? '');
  const [body, setBody] = useState('Test SMS from DriveCars admin.');

  // Sync the default `to` field with the saved admin phone so the panel
  // stays useful when the admin updates their number above.
  useEffect(() => {
    setTo(adminPhone ?? '');
  }, [adminPhone]);

  const sendMut = useMutation({
    mutationFn: () => testSendSms({ to, body }),
  });

  const valid = /^\+\d{6,15}$/.test(to.trim()) && body.trim().length > 0;

  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white">
      <div className="border-b border-[color:var(--bo-line)] px-5 py-3">
        <div className="text-[14px] font-semibold">SMS test</div>
        <div className="text-[11.5px] text-[color:var(--bo-mute)]">
          Sends an arbitrary message via Twilio. Requires TWILIO_* env vars on the api process.
        </div>
      </div>
      <div className="space-y-3 px-5 py-5">
        <TextField
          label="To (E.164 phone)"
          type="tel"
          value={to}
          onChange={setTo}
          placeholder="+12019480710"
          hint="Plus sign, country code, number — e.g. +12019480710."
        />
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
            Message
          </span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-[color:var(--bo-line)] bg-white px-3 py-2 text-[13px] text-[color:var(--bo-ink)] focus:border-[color:var(--bo-mute)] focus:outline-none"
          />
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => sendMut.mutate()}
            disabled={!valid || sendMut.isPending}
            className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sendMut.isPending ? 'Sending…' : 'Send test SMS'}
          </button>
          {sendMut.isSuccess ? (
            <span className="text-[12.5px] text-[#166534]">
              Sent. SID:{' '}
              <span className="font-mono text-[11.5px]">{sendMut.data.sid || 'n/a'}</span>
            </span>
          ) : null}
          {sendMut.error ? (
            <span className="text-[12.5px] text-[#9f1239]">{smsErrorMessage(sendMut.error)}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const WA_SAMPLE_VARS: WhatsAppTestVars = {
  car: 'Mercedes V-Class',
  location: 'Marrakech Menara Airport, Marrakech',
  dates: '2026-06-01 to 2026-06-05',
  price: 'EUR 100.00/day',
};

function WhatsAppTestPanel({ adminPhone }: { adminPhone?: string }) {
  const [to, setTo] = useState(adminPhone ?? '');
  const [vars, setVars] = useState<WhatsAppTestVars>(WA_SAMPLE_VARS);

  useEffect(() => {
    setTo(adminPhone ?? '');
  }, [adminPhone]);

  const sendMut = useMutation({
    mutationFn: () => testSendWhatsApp({ to, vars }),
  });

  const valid =
    /^\+[1-9]\d{6,14}$/.test(to.trim()) &&
    Object.values(vars).every((v) => v.trim().length > 0);

  const setVar = (k: keyof WhatsAppTestVars) => (v: string) =>
    setVars((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white">
      <div className="border-b border-[color:var(--bo-line)] px-5 py-3">
        <div className="text-[14px] font-semibold">WhatsApp test (bid invite)</div>
        <div className="text-[11.5px] text-[color:var(--bo-mute)]">
          Sends the approved bid-invite template (WHATSAPP_BID_TEMPLATE_SID) via Twilio. The
          link variable is a sample placeholder. Requires TWILIO_* + WHATSAPP_BID_TEMPLATE_SID
          env vars on the api process.
        </div>
      </div>
      <div className="space-y-3 px-5 py-5">
        <TextField
          label="To (E.164 phone)"
          type="tel"
          value={to}
          onChange={setTo}
          placeholder="+12019480710"
          hint="Plus sign, country code, number. Must have joined the WhatsApp sandbox / be reachable by your sender."
        />
        <TextField label="Car ({{1}})" value={vars.car} onChange={setVar('car')} />
        <TextField label="Location ({{2}})" value={vars.location} onChange={setVar('location')} />
        <TextField label="Dates ({{3}})" value={vars.dates} onChange={setVar('dates')} />
        <TextField label="Indicative rate ({{4}})" value={vars.price} onChange={setVar('price')} />
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => sendMut.mutate()}
            disabled={!valid || sendMut.isPending}
            className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sendMut.isPending ? 'Sending…' : 'Send test WhatsApp'}
          </button>
          {sendMut.isSuccess ? (
            <span className="text-[12.5px] text-[#166534]">
              Sent. SID:{' '}
              <span className="font-mono text-[11.5px]">{sendMut.data.sid || 'n/a'}</span>
            </span>
          ) : null}
          {sendMut.error ? (
            <span className="text-[12.5px] text-[#9f1239]">
              {whatsAppErrorMessage(sendMut.error)}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─── Email settings — structured form ───────────────────────────────

type EmailLang = 'en' | 'fr' | 'ar' | 'es';

interface EmailLocaleMap {
  en?: string;
  fr?: string;
  ar?: string;
  es?: string;
}

interface EmailSettingsValue {
  fromName?: string;
  fromAddress?: string;
  adminEmail?: string;
  header?: EmailLocaleMap;
  footer?: EmailLocaleMap;
}

const EMAIL_LANGS: Array<{ code: EmailLang; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'ar', label: 'العربية' },
  { code: 'es', label: 'Español' },
];

function EmailSettingsEditor() {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['admin', 'configs', 'email-settings'],
    queryFn: ({ signal }) => fetchAdminConfig('email-settings', signal),
    // Treat the namespace not-yet-existing (404) as an empty config — the
    // first save will create the row.
    retry: false,
  });

  const initial = useMemo<EmailSettingsValue>(
    () => (detail.data?.value as EmailSettingsValue | null) ?? {},
    [detail.data],
  );
  const [draft, setDraft] = useState<EmailSettingsValue>(initial);
  const [lang, setLang] = useState<EmailLang>('en');

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const saveMut = useMutation({
    mutationFn: ({ value }: { value: EmailSettingsValue }) =>
      saveAdminConfig('email-settings', value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'configs', 'email-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'configs', 'list'] });
    },
  });

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);

  const setField = <K extends keyof EmailSettingsValue>(
    key: K,
    value: EmailSettingsValue[K],
  ) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };
  const setLocaleField = (slot: 'header' | 'footer', value: string) => {
    setDraft((d) => ({
      ...d,
      [slot]: { ...(d[slot] ?? {}), [lang]: value },
    }));
  };

  const isMissing = detail.error && (detail.error as Error).message.includes('404');

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--bo-line)] px-5 py-3">
          <div>
            <div className="text-[14px] font-semibold">Email</div>
            <div className="font-mono text-[11px] text-[color:var(--bo-mute)]">email-settings</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDraft(initial)}
              disabled={!dirty}
              className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => saveMut.mutate({ value: draft })}
              disabled={!dirty || saveMut.isPending}
              className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        {detail.error && !isMissing ? (
          <div className="border-b border-[color:var(--bo-line)] bg-[#fff1f2] px-5 py-2 text-[12.5px] text-[#9f1239]">
            Couldn&apos;t load namespace: {(detail.error as Error).message}
          </div>
        ) : null}
        {saveMut.error ? (
          <div className="border-b border-[color:var(--bo-line)] bg-[#fff1f2] px-5 py-2 text-[12.5px] text-[#9f1239]">
            Save failed: {(saveMut.error as Error).message}
          </div>
        ) : null}
        {saveMut.isSuccess && !dirty ? (
          <div className="border-b border-[color:var(--bo-line)] bg-[#dcfce7] px-5 py-2 text-[12.5px] text-[#166534]">
            Saved.
          </div>
        ) : null}

        <div className="space-y-6 px-5 py-5">
          <Section
            title="SMTP transport"
            desc="Driver, host, port, encryption, username and password are configured via the apps/api .env (SMTP_HOST / SMTP_PORT / SMTP_USER / SMTP_PASSWORD) — not editable here."
          >
            <EmailTransportSummary />
          </Section>

          <Section
            title="From"
            desc="Display name and address used on the From: header of every outbound email. Falls back to SMTP_FROM_NAME / SMTP_FROM_ADDRESS when blank."
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField
                label="Email Form Name"
                placeholder="DriveCars"
                value={draft.fromName ?? ''}
                onChange={(v) => setField('fromName', v)}
              />
              <TextField
                label="Email Form Address"
                placeholder="no-reply@drivecars.ai"
                value={draft.fromAddress ?? ''}
                onChange={(v) => setField('fromAddress', v)}
              />
            </div>
          </Section>

          <Section
            title="Admin email"
            desc="Mailbox that receives operational notifications. Will get all admin-routed emails (booking confirmations, cancellations, etc.)."
          >
            <TextField
              label="Admin Email"
              placeholder="master@drivecars.ai"
              value={draft.adminEmail ?? ''}
              onChange={(v) => setField('adminEmail', v)}
            />
          </Section>

          <Section
            title="Email header & footer"
            desc="HTML wrapped above and below every rendered template. Authored per language."
          >
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1 border-b border-[color:var(--bo-line)]">
                {EMAIL_LANGS.map((opt) => {
                  const active = opt.code === lang;
                  return (
                    <button
                      key={opt.code}
                      type="button"
                      onClick={() => setLang(opt.code)}
                      className={`-mb-px inline-flex h-8 items-center rounded-t-md border px-3 text-[12px] font-medium transition ${
                        active
                          ? 'border-[color:var(--bo-line)] border-b-white bg-white text-[color:var(--bo-ink)]'
                          : 'border-transparent text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <CodeArea
                label="Header"
                value={draft.header?.[lang] ?? ''}
                onChange={(v) => setLocaleField('header', v)}
                placeholder="<table>…</table>"
              />
              <CodeArea
                label="Footer"
                value={draft.footer?.[lang] ?? ''}
                onChange={(v) => setLocaleField('footer', v)}
                placeholder="<table>…</table>"
              />
            </div>
          </Section>
        </div>
      </div>

      <EmailTestPanel adminEmail={draft.adminEmail} />
    </div>
  );
}

function CodeArea({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
        {label}
      </span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={6}
        spellCheck={false}
        className="w-full resize-y rounded-md border border-[color:var(--bo-line)] bg-white px-3 py-2 font-mono text-[12.5px] leading-relaxed text-[color:var(--bo-ink)] focus:border-[color:var(--bo-mute)] focus:outline-none"
      />
    </label>
  );
}

function EmailTransportSummary() {
  const cfg = useQuery({
    queryKey: ['admin', 'email', 'config'],
    queryFn: ({ signal }) => fetchEmailTransportConfig(signal),
  });
  if (cfg.isLoading) {
    return (
      <div className="rounded-md border border-dashed border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-4 py-3 text-[12.5px] text-[color:var(--bo-mute)]">
        Loading transport config…
      </div>
    );
  }
  if (cfg.error || !cfg.data) {
    return (
      <div className="rounded-md border border-dashed border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-4 py-3 text-[12.5px] text-[#9f1239]">
        Couldn&apos;t read transport config: {(cfg.error as Error | null)?.message ?? 'unknown'}
      </div>
    );
  }
  const data = cfg.data;
  const rows: Array<[string, string]> = [
    ['Driver', data.driver || '—'],
    ['Host', data.host || '—'],
    ['Port', data.port || '—'],
    ['Encryption', data.encryption || 'none'],
    ['Username', data.username || '—'],
    ['Password', data.hasPassword ? '••••••••' : '— (none)'],
  ];
  return (
    <div className="overflow-hidden rounded-md border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)]">
      <dl className="grid grid-cols-1 divide-y divide-[color:var(--bo-line)] sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-3 px-4 py-2">
            <dt className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
              {k}
            </dt>
            <dd className="font-mono text-[12px] text-[color:var(--bo-ink)]">{v}</dd>
          </div>
        ))}
      </dl>
      {!data.configured ? (
        <div className="border-t border-[color:var(--bo-line)] bg-[#fff7ed] px-4 py-2 text-[11.5px] text-[#9a3412]">
          SMTP_HOST is not set on apps/api — outbound mail is disabled.
        </div>
      ) : null}
    </div>
  );
}

function EmailTestPanel({ adminEmail }: { adminEmail?: string }) {
  const [to, setTo] = useState(adminEmail ?? '');
  useEffect(() => {
    setTo(adminEmail ?? '');
  }, [adminEmail]);

  const sendMut = useMutation({
    mutationFn: () => testSendEmail({ to }),
  });

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim());

  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white">
      <div className="border-b border-[color:var(--bo-line)] px-5 py-3">
        <div className="text-[14px] font-semibold">Email testing</div>
        <div className="text-[11.5px] text-[color:var(--bo-mute)]">
          Sends a one-shot message via the configured SMTP transport. Uses the saved From / header /
          footer.
        </div>
      </div>
      <div className="space-y-3 px-5 py-5">
        <TextField
          label="Email"
          value={to}
          onChange={setTo}
          placeholder="ops@drivecars.ai"
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => sendMut.mutate()}
            disabled={!valid || sendMut.isPending}
            className="inline-flex h-9 items-center rounded-lg bg-[#2a6fdb] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-[#1f54b6] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sendMut.isPending ? 'Sending…' : 'Send Email Test'}
          </button>
          {sendMut.isSuccess ? (
            <span className="text-[12.5px] text-[#166534]">Sent.</span>
          ) : null}
          {sendMut.error ? (
            <span className="text-[12.5px] text-[#9f1239]">
              Failed: {(sendMut.error as Error).message}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
